import React, {
	useEffect,
	useState,
} from 'react';
import {
	Card,
	Col,
	ConfigProvider,
	Row,
	Skeleton,
} from 'antd';
import {
	useHistory,
	useLocation,
} from 'react-router-dom';
import {
	EstimatorModeTypes,
	FixtureTypes,
} from '@shared/utils/constants';
import { useAuth } from '@client/lib/auth';
import { useFocusShortcuts } from '@client/utils/hooks/useFocusShortcuts';
import ModePicker from './components/ModePicker';
import TopBar from './components/TopBar';
import EstimatedPnL from './components/EstimatedPnL';
import EstimateConfigurationCard from './components/EstimateConfigurationCard';
import CargosCard from './components/CargosCard';
import EstimateProvider from './context/EstimatorContext';
import PortRotationCard from './PortRotationCard';
import { ESTIMATE_SHORTCUT_TARGETS } from './helpers/estimateShortcuts';
import PortRotationProvider from './context/PortRotationContext';
import {
	CentralStoreProvider,
	useCentralStore,
} from './context/CentralStoreContext';
import CargoProvider from './context/CargoContext';
import MapCard from './MapCard';
import GridView from './GridView';
import EstimateChangesProvider, { useEstimateChangesProvider } from './context/EstimateChangesContext';
import GridChangesProvider, { useGridChanges } from './context/GridChangesContext';
import RobsAndPrices from './components/RobsAndPrices';
import TcEstimateDetails from './TcEstimateDetails';
import styles from './styles/EstimateDetailsScreen.module.css';

const LoadingCards = () => (
	<Row gutter={[8, 8]}>
		<Col span={24}>
			<Card>
				<Skeleton loading />
			</Card>
		</Col>
		<Col span={24}>
			<Card>
				<Skeleton loading />
			</Card>
		</Col>
		<Col span={24}>
			<Card>
				<Skeleton loading />
			</Card>
		</Col>
	</Row>
);

const DisableWhileSaving = ({ children }: { children: React.ReactNode }) => {
	const { saving } = useEstimateChangesProvider();
	const { savingGrid } = useGridChanges();

	return (
		<ConfigProvider componentDisabled={saving || savingGrid}>
			{children}
		</ConfigProvider>
	);
};

const EstimatorContent = ({ mode }: { mode: EstimatorModeTypes }) => {
	useFocusShortcuts(ESTIMATE_SHORTCUT_TARGETS);

	const {
		refreshEverything,
		fixtureDetails,
		fixtureDetailsLoading,
		selectedEstimate,
		selectedEstimateId,
		estimate,
	} = useCentralStore();

	useEffect(() => {
		if (mode) {
			refreshEverything();
		}
	}, [mode, refreshEverything]);

	if (mode === EstimatorModeTypes.MAP) {
		return (
			<Row gutter={[8, 8]}>
				<Col span={24}>
					<MapCard />
				</Col>
				<Col span={24}>
					<PortRotationCard mode={mode} />
				</Col>
			</Row>
		);
	}

	if (mode === EstimatorModeTypes.GRID) {
		return (<GridView />);
	}

	if (selectedEstimate == null) {
		return (<LoadingCards />);
	}

	if (selectedEstimate.type === FixtureTypes.SPOT) {
		return (
			<Row gutter={[8, 8]}>
				<Col span={24}>
					<EstimateConfigurationCard />
				</Col>
				<Col span={24}>
					<RobsAndPrices />
				</Col>
				<Col span={24}>
					<CargosCard mode={mode} />
				</Col>
				<Col span={24}>
					<PortRotationCard mode={mode} />
				</Col>
			</Row>
		);
	}

	if (selectedEstimate.type === FixtureTypes.TC_OUT) {
		if (fixtureDetails != null) {
			return (
				<TcEstimateDetails mode={mode} />
			);
		}

		const isLoadingTcOut = estimate?.id !== selectedEstimateId ||
			(estimate?.Fixture != null && fixtureDetailsLoading);

		if (isLoadingTcOut) {
			return (<LoadingCards />);
		}
	}

	return (
		<Row gutter={[8, 8]}>
			<Col span={24}>
				<EstimateConfigurationCard />
			</Col>
			<Col span={24}>
				<RobsAndPrices />
			</Col>
			<Col span={24}>
				<CargosCard mode={mode} />
			</Col>
			<Col span={24}>
				<PortRotationCard mode={mode} />
			</Col>
		</Row>
	);
};

const EstimateDetailsScreen = () => {
	const location = useLocation();
	const history = useHistory();
	const { userInfo } = useAuth();

	// parse mode from URL (fallback to NORMAL)
	const params = new URLSearchParams(location.search);
	const initial = (params.get('mode') as EstimatorModeTypes) ||
		EstimatorModeTypes.NORMAL;

	const [mode, setMode] = useState<EstimatorModeTypes>(initial);

	useEffect(() => {
		if (userInfo?.preferredEstimateView) {
			setMode(userInfo.preferredEstimateView);
		}
	}, [userInfo?.preferredEstimateView]);

	// whenever mode changes, push it into the URL
	useEffect(() => {
		const newParams = new URLSearchParams(location.search);
		newParams.set('mode', mode);
		history.replace({
			pathname: location.pathname,
			search: newParams.toString(),
		});
	}, [mode, history, location.pathname, location.search]);

	return (
		<CentralStoreProvider>
			<CargoProvider>
				<EstimateProvider>
					<PortRotationProvider>
						<EstimateChangesProvider>
							<GridChangesProvider>
								<DisableWhileSaving>
									<div style={{ padding: 8 }}>
										<Row gutter={8} className={styles.stickyHeader}>
											<Col span={3}>
												<ModePicker mode={mode} setMode={setMode} />
											</Col>
											<Col span={21}>
												<TopBar mode={mode} />
											</Col>
										</Row>
										<Row gutter={[8, 8]}>
											<Col span={mode === EstimatorModeTypes.GRID ? 24 : 18}>
												<EstimatorContent mode={mode} />
											</Col>
											{mode !== EstimatorModeTypes.GRID && (
												<Col span={6}>
													<Card styles={{ body: { padding: 0 } }}>
														<EstimatedPnL />
													</Card>
												</Col>
											)}
										</Row>
									</div>
								</DisableWhileSaving>
							</GridChangesProvider>
						</EstimateChangesProvider>
					</PortRotationProvider>
				</EstimateProvider>
			</CargoProvider>
		</CentralStoreProvider>
	);
};

export default EstimateDetailsScreen;
